import type { UtilityDefinition } from '@utilities/types';

import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router';

import { StatusPill, type StatusPillVariant } from './StatusPill';

interface UtilityCardProps {
  lastUsedText?: string;
  metaText: string;
  statusLabel: string;
  statusVariant: StatusPillVariant;
  utility: UtilityDefinition;
}

export const UtilityCard = ({ lastUsedText, metaText, statusLabel, statusVariant, utility }: UtilityCardProps) => {
  const navigate = useNavigate();
  const Icon = utility.icon;

  return (
    <Card sx={{ height: '100%' }} variant="outlined">
      <CardActionArea
        onClick={() => {
          void navigate(utility.path);
        }}
        sx={{
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          height: '100%',
          justifyContent: 'flex-start',
          p: 2,
        }}
      >
        <Stack alignItems="flex-start" direction="row" spacing={1.25}>
          <Box
            sx={theme => ({
              alignItems: 'center',
              bgcolor: theme.palette.primary.light,
              borderRadius: 2,
              color: theme.palette.primary.dark,
              display: 'flex',
              flexShrink: 0,
              height: 34,
              justifyContent: 'center',
              width: 34,
            })}
          >
            <Icon size={16} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{utility.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              Owner — {utility.owner}
            </Typography>
          </Box>
        </Stack>

        <Typography color="text.secondary" sx={{ flexGrow: 1, fontSize: 12.5 }} variant="body2">
          {utility.description}
        </Typography>

        <Stack alignItems="center" direction="row" spacing={0.75}>
          <StatusPill label={statusLabel} variant={statusVariant} />
          <Typography color="text.disabled" variant="caption">
            ·
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {metaText}
          </Typography>
        </Stack>

        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ borderColor: 'divider', borderTop: '1px solid', mt: 0.25, pt: 1.25, width: '100%' }}
        >
          <Typography color="text.disabled" variant="caption">
            {lastUsedText}
          </Typography>
          <Stack
            alignItems="center"
            direction="row"
            spacing={0.5}
            sx={theme => ({ color: theme.palette.text.heading, fontSize: 12, fontWeight: 600 })}
          >
            Open <ArrowRight size={14} />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};
